import React, { useState, useRef } from 'react';
import { 
  encodeMessage, 
  decodeMessage, 
  validateImage, 
  validateMessage, 
  validateKey 
} from './utils/steganography';
import './index.css';

function App() {
  const [originalImage, setOriginalImage] = useState(null);
  const [encodedImageURL, setEncodedImageURL] = useState(null);
  const [message, setMessage] = useState('');
  const [decodedMessage, setDecodedMessage] = useState('');
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEncoding, setIsEncoding] = useState(false);
  const [isDecoding, setIsDecoding] = useState(false);
  const fileInputRef = useRef(null);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    clearMessages();
    
    if (!file) {
      setOriginalImage(null);
      return;
    }

    try {
      validateImage(file);
      setOriginalImage(file);
      setSuccess(`Image loaded successfully (${(file.size / 1024).toFixed(1)} KB)`);
      // Clear previous results when new image is loaded
      setEncodedImageURL(null);
      setDecodedMessage('');
    } catch (err) {
      setError(err.message);
      setOriginalImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEncode = async () => {
    clearMessages();
    
    if (!originalImage) {
      setError('Please select an image first');
      return;
    }

    try {
      validateMessage(message);
      validateKey(key);
      
      setIsEncoding(true);
      
      // Create canvas and load image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        try {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const encodedData = encodeMessage(imageData, message, key);
          
          ctx.putImageData(encodedData, 0, 0);
          const url = canvas.toDataURL('image/png');
          
          setEncodedImageURL(url);
          setSuccess('Message encoded successfully! You can now download the image.');
          setIsEncoding(false);
        } catch (err) {
          setError(err.message);
          setIsEncoding(false);
        }
      };
      
      img.onerror = () => {
        setError('Failed to load image');
        setIsEncoding(false);
      };
      
      img.src = URL.createObjectURL(originalImage);
      
    } catch (err) {
      setError(err.message);
      setIsEncoding(false);
    }
  };

  const handleDecode = async () => {
    clearMessages();
    
    if (!originalImage) {
      setError('Please select an image first');
      return;
    }

    try {
      validateKey(key);
      
      setIsDecoding(true);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        try {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const decoded = decodeMessage(imageData, key);
          
          setDecodedMessage(decoded);
          setSuccess('Message decoded successfully!');
          setIsDecoding(false);
        } catch (err) {
          setError(err.message);
          setIsDecoding(false);
        }
      };
      
      img.onerror = () => {
        setError('Failed to load image');
        setIsDecoding(false);
      };
      
      img.src = URL.createObjectURL(originalImage);
      
    } catch (err) {
      setError(err.message);
      setIsDecoding(false);
    }
  };

  const handleDownload = () => {
    if (!encodedImageURL) return;
    
    const link = document.createElement('a');
    link.download = 'stegavibe-encoded.png';
    link.href = encodedImageURL;
    link.click();
  };

  const handleClear = () => {
    setOriginalImage(null);
    setEncodedImageURL(null);
    setMessage('');
    setDecodedMessage('');
    setKey('');
    clearMessages();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const remainingChars = 128 - message.length;

  return (
    <div className="app">
      <div className="container">
        <div className="header">
          <h1 className="title">StegaVibe</h1>
          <p className="subtitle">Hide secret messages in images using advanced steganography</p>
        </div>

        <div className="info-box">
          <h4>How it works:</h4>
          <ul>
            <li>Upload an image (max 1MB, PNG/JPEG/GIF)</li>
            <li>Enter your secret message (max 128 characters)</li>
            <li>Set an encryption key (min 4 characters)</li>
            <li>Encode to hide the message or decode to reveal it</li>
          </ul>
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <div className="form-group">
          <label className="label">Select Image</label>
          <div className="file-input">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              id="file-input"
            />
            <label 
              htmlFor="file-input" 
              className={`file-input-label ${originalImage ? 'has-file' : ''}`}
            >
              {originalImage 
                ? `📁 ${originalImage.name} (${(originalImage.size / 1024).toFixed(1)} KB)`
                : '📁 Click to select an image file'
              }
            </label>
          </div>
        </div>

        <div className="form-group">
          <label className="label">Encryption Key</label>
          <input
            type="password"
            className="input"
            placeholder="Enter a secure key (min 4 characters)"
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="label">Secret Message</label>
          <textarea
            className="textarea"
            placeholder="Enter your secret message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={128}
          />
          <div className={`character-count ${remainingChars < 20 ? 'warning' : ''}`}>
            {remainingChars} characters remaining
          </div>
        </div>

        <div className="button-group">
          <button
            className="button button-primary"
            onClick={handleEncode}
            disabled={isEncoding || isDecoding || !originalImage || !message.trim() || !key.trim()}
          >
            {isEncoding && <span className="loading"></span>}
            {isEncoding ? 'Encoding...' : '🔒 Encode Message'}
          </button>
          
          <button
            className="button button-secondary"
            onClick={handleDecode}
            disabled={isEncoding || isDecoding || !originalImage || !key.trim()}
          >
            {isDecoding && <span className="loading"></span>}
            {isDecoding ? 'Decoding...' : '🔓 Decode Message'}
          </button>
        </div>

        <div className="button-group">
          <button
            className="button button-secondary"
            onClick={handleClear}
            disabled={isEncoding || isDecoding}
          >
            🗑️ Clear All
          </button>
        </div>

        {encodedImageURL && (
          <div className="result-section">
            <h3 className="result-title">✅ Encoded Image</h3>
            <img src={encodedImageURL} alt="Encoded" className="image-preview" />
            <button className="download-button" onClick={handleDownload}>
              ⬇️ Download Encoded Image
            </button>
          </div>
        )}

        {decodedMessage && (
          <div className="result-section">
            <h3 className="result-title">🔍 Decoded Message</h3>
            <div className="decoded-message">
              {decodedMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
